import * as THREE from 'three';
import { cloth, emis, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { Plumage } from './crow.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * What the skills learned in the tree bring onto the field (docs/PODERES.md §7): the warrior's decoy (a
 * training dummy on a post with a red pennant), the ranger's hawk (a goshawk on fingered wings, rigged:
 * it circles, beats on and stoops talons first), the artificer's repair drone (an iron box on spinning rotors with a cyan lamp) and the cleric's guard totem (a carved standing stone with cold holy runes) and the warrior's guard banner (an iron-shod pole with a long red
 * war banner and a steel shield boss). No gold.
 */

const POST = std('#4a3424', { roughness: 0.9, flatShading: true });
const SACK = cloth('#6b675f', { flatShading: true });
const SACK_DK = cloth('#4c4943', { flatShading: true });
const ROPE = std('#8a8577', { roughness: 0.95 });
const PENNANT = cloth('#9a2a24', { side: THREE.DoubleSide });
const IRON = metal('#4a4f57', { roughness: 0.5 });

/** The warrior's decoy: a straw-and-sack dummy on a post, arms out, a red pennant over it. Ticks wave the pennant. */
export const decoy = (o?: BaseOpts): Model => {
  const c = begin(o, 0.45, 61);
  const { g } = c;
  limb(g, V(0, 0, 0), V(0, 1.75, 0), 0.055, 0.045, POST, 6);
  // The cross-bar the arms hang on, and the stake's foot.
  limb(g, V(-0.5, 1.15, 0), V(0.5, 1.15, 0), 0.04, 0.04, POST, 6);
  for (const k of [-1, 1]) limb(g, V(0, 0.02, 0), V(k * 0.22, -0.02, k * 0.1), 0.03, 0.02, POST, 5);
  // Body: a stuffed sack tied at the waist; arms of sack over the bar.
  const body = M(new THREE.CylinderGeometry(0.2, 0.24, 0.62, 8), SACK, g);
  body.position.y = 0.98;
  ring(g, 0.215, 0.02, ROPE, 0.78, 4, 12);
  ring(g, 0.205, 0.02, ROPE, 1.2, 4, 12);
  for (const k of [-1, 1]) {
    const arm = limb(g, V(k * 0.16, 1.17, 0), V(k * 0.5, 1.13, 0), 0.075, 0.06, SACK_DK, 6);
    arm.castShadow = true;
  }
  // Head: a sack ball with a stitched cross for a face.
  const head = M(new THREE.SphereGeometry(0.17, 10, 8), SACK, g);
  head.position.y = 1.47;
  head.scale.set(1, 1.05, 0.95);
  for (const k of [-1, 1]) {
    const st = M(new THREE.BoxGeometry(0.11, 0.018, 0.02), ROPE, g);
    st.position.set(0, 1.49, 0.16);
    st.rotation.z = k * 0.78;
  }
  // A dented iron plate tied on its chest: the thing they're drawn to hit.
  const plate = M(new THREE.CylinderGeometry(0.15, 0.15, 0.03, 8), IRON, g);
  plate.position.set(0, 1.0, 0.2);
  plate.rotation.x = Math.PI / 2;
  // The pennant on top, waving.
  limb(g, V(0, 1.6, 0), V(0, 2.15, 0), 0.018, 0.015, POST, 5);
  const flag = G(g, 0, 2.08, 0);
  const cloth1 = M(new THREE.PlaneGeometry(0.42, 0.22, 4, 1), PENNANT, flag);
  cloth1.position.x = 0.22;
  const pos = cloth1.geometry.attributes.position as THREE.BufferAttribute;
  const base = Float32Array.from(pos.array as Float32Array);
  const t = (n: number): void => {
    for (let i = 0; i < pos.count; i++) {
      const x = base[i * 3] as number;
      const w = (x + 0.21) / 0.42;
      pos.setZ(i, Math.sin(n * 1.6 + w * 3.2) * 0.07 * w);
      pos.setY(i, (base[i * 3 + 1] as number) - w * w * 0.05);
    }
    pos.needsUpdate = true;
  };
  t(0);
  return done(c, 2.2, t);
};

/** Height of the hawk's origin over the ground while it hovers (the clips stoop it from here onto its prey). */
export const HAWK_HOVER = 1.6;

// Slate-brown back and wings, a cold cream breast, the dark of the flight feathers' tips and the tail's brick red
// with its dark bar: a goshawk's colours, readable from the game camera above as a dark cross with a red fan.
const H_BACK = std('#4d3d32', { roughness: 0.75, flatShading: true, side: THREE.DoubleSide, bumpScale: 0.02 });
const H_DARK = std('#221a16', { roughness: 0.7, flatShading: true, side: THREE.DoubleSide, bumpScale: 0.02 });
const H_PALE = std('#cbc4b6', { roughness: 0.85, flatShading: true, side: THREE.DoubleSide, bumpScale: 0.02 });
const H_MID = std('#7d6a5a', { roughness: 0.8, flatShading: true, side: THREE.DoubleSide, bumpScale: 0.02 });
const H_TAIL = std('#6e3b2a', { roughness: 0.75, flatShading: true, side: THREE.DoubleSide, bumpScale: 0.02 });
const H_EYE = emis('#ffe0a6', '#ff8a2a', 1.1);

/**
 * The ranger's hawk: a goshawk on broad fingered wings, hovering. Feather blades like the soot crow's (models/crow.ts)
 * on a bird's bones, so it flies by its clips (anim/creatures/hawk.ts): it circles, beats on, and stoops talons first.
 * The ranger's mark: a red jess on each leg. At rest the wings are spread flat; without the rig (`?anim=0`) its ticks
 * beat them over four frames.
 *
 * Bones as the crow's: on the right wing (+X) +Z on `arm` lifts it, +Y sweeps it back; +Y on `hand` folds the
 * primaries back. +X pitches `body` and `head` nose down, lifts the `tail` and swings the talons (`legs`) back.
 */
export const hawk = (o?: BaseOpts): Model => {
  const c = begin(o, 0.4, 62);
  const { g, rng, R } = c;
  g.position.y = HAWK_HOVER;
  const body = joint(g, V(0, 0, 0), 'body');
  const head = joint(body.abs, V(0, 0.04, 0.22), 'head');
  const tail = joint(body.abs, V(0, 0.01, -0.2), 'tail');
  const legs = joint(body.abs, V(0, -0.07, 0.0), 'legs');
  /** Hangs a set of blades on a bone: vanes in `vane`, the tips in `tip`. */
  const wear = (pl: Plumage, parent: THREE.Object3D, vane: THREE.Material, tip: THREE.Material): void => {
    const gm = pl.geometry();
    M(gm.geo, vane, parent);
    M(gm.edge, tip, parent);
  };

  /* ---------- body: a teardrop trunk, the cream breast under it, mantle feathers over the shoulders ---------- */
  const prof = [V(0, -0.24, 0), V(0.05, -0.2, 0), V(0.095, -0.1, 0), V(0.115, 0.02, 0), V(0.11, 0.13, 0), V(0.08, 0.21, 0), V(0, 0.26, 0)];
  const trunk = M(new THREE.LatheGeometry(prof.map((p) => new THREE.Vector2(p.x, p.y)), 8), H_BACK, body.abs);
  trunk.rotation.x = Math.PI / 2;
  trunk.scale.set(1, 1, 0.85);
  const breast = M(new THREE.SphereGeometry(0.1, 8, 6), H_PALE, body.abs);
  breast.position.set(0, -0.035, 0.07);
  breast.scale.set(0.95, 0.8, 1.6);
  // Dark bars across the breast, the goshawk's.
  for (let i = 0; i < 3; i++) {
    const bar = M(new THREE.BoxGeometry(0.13 - i * 0.02, 0.012, 0.014), H_DARK, body.abs);
    bar.position.set(0, -0.085 + i * 0.004, 0.13 - i * 0.06);
  }
  const mantle = new Plumage(1, rng);
  for (const k of [-1, 1]) for (let i = 0; i < 3; i++) mantle.feather(V(k * 0.05, 0.085, 0.14 - i * 0.09), V(k * 0.35, -0.1, -1), 0.14, 0.07, false, -1, 0.1);
  wear(mantle, body.abs, H_BACK, H_DARK);

  /* ---------- head: pale cheeks under a dark cap, a stripe under the eye, the hooked beak ---------- */
  const skull = M(new THREE.SphereGeometry(0.075, 8, 6), H_PALE, head.abs);
  skull.position.set(0, 0.06, 0.29);
  skull.scale.set(0.92, 0.9, 1.15);
  const cap = M(new THREE.SphereGeometry(0.079, 8, 4, 0, Math.PI * 2, 0, Math.PI * 0.42), H_BACK, head.abs);
  cap.position.set(0, 0.07, 0.275);
  cap.scale.set(0.95, 0.9, 1.25);
  cap.rotation.x = -0.25;
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.016, 6, 4), H_EYE, head.abs).position.set(k * 0.048, 0.075, 0.335);
    // The pale brow over the eye and the dark stripe from it down the cheek.
    const brow = M(new THREE.BoxGeometry(0.012, 0.012, 0.08), H_PALE, head.abs);
    brow.position.set(k * 0.058, 0.098, 0.315);
    brow.rotation.y = k * 0.35;
    const stripe = M(new THREE.BoxGeometry(0.012, 0.045, 0.022), H_DARK, head.abs);
    stripe.position.set(k * 0.06, 0.04, 0.325);
    stripe.rotation.x = -0.35;
  }
  const beak = M(new THREE.ConeGeometry(0.026, 0.075, 5), H_DARK, head.abs);
  beak.position.set(0, 0.05, 0.385);
  beak.rotation.x = Math.PI / 2 + 0.35;
  const hook = M(new THREE.ConeGeometry(0.012, 0.035, 4), H_DARK, head.abs);
  hook.position.set(0, 0.03, 0.418);
  hook.rotation.x = Math.PI - 0.5;

  /* ---------- wings: broad arm (coverts, a pale bar, secondaries) and the hand of six fingered primaries ---------- */
  const skel: Record<string, THREE.Object3D> = { body: body.p, head: head.p };
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const arm = joint(body.abs, V(k * 0.08, 0.04, 0.07), `arm${s}`);
    const hand = joint(arm.abs, V(k * 0.44, 0.04, 0.05), `hand${s}`);
    skel[`arm${s}`] = arm.p;
    skel[`hand${s}`] = hand.p;
    const inner = new Plumage(k, rng);
    inner.plate([V(0.06, 0.045, 0.11), V(0.25, 0.05, 0.13), V(0.45, 0.045, 0.09), V(0.45, 0.038, -0.04), V(0.25, 0.04, -0.06), V(0.06, 0.035, -0.08)]);
    // Secondaries: broad, square-ended, darker at the trailing edge.
    for (let i = 0; i < 7; i++) {
      const u = i / 6;
      inner.feather(V(0.08 + u * 0.36, 0.036, 0.0), V(0.1 * u - 0.04, 0, -1), 0.27 - u * 0.04 + R() * 0.02, 0.1, false, 0, 0.03);
    }
    wear(inner, arm.abs, H_BACK, H_DARK);
    // Coverts shingled over the leading edge, lighter-edged: the bar along the wing.
    const coverts = new Plumage(k, rng);
    for (let i = 0; i < 5; i++) {
      const u = i / 4;
      coverts.feather(V(0.08 + u * 0.36, 0.052, 0.13 - Math.abs(u - 0.5) * 0.04), V(0.12 + u * 0.1, 0.0, -1), 0.13 + R() * 0.02, 0.1, false, -1, 0.05);
    }
    wear(coverts, arm.abs, H_BACK, H_MID);
    const outer = new Plumage(k, rng);
    outer.plate([V(0.43, 0.045, 0.1), V(0.58, 0.045, 0.09), V(0.66, 0.04, 0.02), V(0.58, 0.036, -0.06), V(0.43, 0.036, -0.03)]);
    // Primaries: six spread fingers, the outer four notched (the slots a soaring hawk shows), dark tipped.
    for (let i = 0; i < 6; i++) {
      const u = i / 5;
      const a = 1.0 - u * 1.15;
      outer.feather(V(0.45 + u * 0.13, 0.04, 0.05 + u * 0.03), V(Math.cos(a), 0, -Math.sin(a)), 0.3 + u * 0.1 + R() * 0.02, 0.085 - u * 0.012, false, u > 0.3 ? 1 : 0, 0.04);
    }
    wear(outer, hand.abs, H_BACK, H_DARK);
  }

  /* ---------- tail: a brick-red fan with a dark bar near the end ---------- */
  skel.tail = tail.p;
  const fan = new Plumage(1, rng);
  fan.plate([V(-0.055, 0.02, -0.17), V(0.055, 0.02, -0.17), V(0.05, 0.015, -0.3), V(-0.05, 0.015, -0.3)]);
  for (let i = 0; i < 7; i++) {
    const u = i / 6 - 0.5;
    fan.feather(V(u * 0.09, 0.018 - Math.abs(u) * 0.006, -0.19), V(u * 0.75, 0, -1), 0.3 - Math.abs(u) * 0.05, 0.075, false, 0, 0.02);
  }
  wear(fan, tail.abs, H_TAIL, H_DARK);
  const bar = M(new THREE.BoxGeometry(0.2, 0.006, 0.03), H_DARK, tail.abs);
  bar.position.set(0, 0.024, -0.38);

  /* ---------- talons drawn up under the belly, each with the ranger's red jess ---------- */
  skel.legs = legs.p;
  for (const k of [-1, 1]) {
    const hip = V(k * 0.04, -0.07, 0.0), ankle = V(k * 0.045, -0.15, -0.06);
    limb(legs.abs, hip, ankle, 0.018, 0.012, H_MID, 4);
    for (let i = 0; i < 3; i++) {
      const cl = M(new THREE.ConeGeometry(0.008, 0.055, 3), H_DARK, legs.abs);
      cl.position.set(ankle.x + (i - 1) * 0.014 * k, ankle.y - 0.012, ankle.z + 0.022);
      cl.rotation.set(Math.PI / 2 + 0.7, 0, (i - 1) * 0.3);
    }
    const jess = M(new THREE.BoxGeometry(0.014, 0.004, 0.11), PENNANT, legs.abs);
    jess.position.set(ankle.x, ankle.y + 0.012, ankle.z - 0.06);
    jess.rotation.x = 0.35;
  }

  // Without the rig: four frames of a beat (up, mid, down, mid); 0 is the rest pose the rig is baked from.
  const wingR = skel.armR as THREE.Object3D, wingL = skel.armL as THREE.Object3D;
  const handR = skel.handR as THREE.Object3D, handL = skel.handL as THREE.Object3D;
  const t = (n: number): void => {
    const a = n === 0 ? 0 : ([0.75, 0.15, -0.55, 0.15][(n - 1) & 3] as number);
    const f = n === 0 ? 0 : ([0.25, 0.1, -0.2, 0.1][(n - 1) & 3] as number);
    wingR.rotation.z = a;
    wingL.rotation.z = -a;
    handR.rotation.z = f;
    handL.rotation.z = -f;
    g.position.y = HAWK_HOVER + (n === 0 ? 0 : ([0.04, 0, -0.05, 0][(n - 1) & 3] as number));
  };
  t(0);
  return done(c, HAWK_HOVER + 0.3, t, undefined, bonesOf(c, skel));
};

const RUNESTONE = stone('#7c8088', { flatShading: true });
const RUNESTONE_DK = stone('#55585f', { flatShading: true });
const RUNE = emis('#f3f6ff', '#bcd2ff', 1.4);

/** The cleric's guard totem: a carved standing stone, cold runes down its face and a light on top. Ticks pulse the light. */
export const totem = (o?: BaseOpts): Model => {
  const c = begin(o, 0.5, 63);
  const { g } = c;
  const foot = M(new THREE.CylinderGeometry(0.34, 0.4, 0.14, 7), RUNESTONE_DK, g);
  foot.position.y = 0.07;
  const shaft = M(new THREE.CylinderGeometry(0.18, 0.24, 1.3, 6), RUNESTONE, g);
  shaft.position.y = 0.79;
  // Bands carved round it, and runes on its face.
  for (const y of [0.4, 0.95, 1.35]) ring(g, 0.215 - y * 0.03, 0.025, RUNESTONE_DK, y, 4, 6);
  for (let i = 0; i < 4; i++) {
    const r = M(new THREE.BoxGeometry(0.1, 0.03, 0.02), RUNE, g);
    r.position.set(0, 0.55 + i * 0.22, 0.205 - i * 0.012);
    r.rotation.z = i & 1 ? 0.6 : -0.6;
  }
  const cap = M(new THREE.ConeGeometry(0.24, 0.3, 6), RUNESTONE_DK, g);
  cap.position.y = 1.59;
  const orb = M(new THREE.SphereGeometry(0.09, 10, 8), RUNE, g);
  orb.position.y = 1.82;
  const glow = glowSprite(g, '#dfe8ff', 0.9, V(0, 1.82, 0), 0.7, true);
  const gm = glow.material as THREE.SpriteMaterial;
  const t = (n: number): void => {
    gm.opacity = 0.5 + 0.25 * Math.sin(n * 0.8);
    orb.scale.setScalar(1 + 0.08 * Math.sin(n * 0.8));
  };
  t(0);
  return done(c, 2.0, t);
};

const DRONE_IRON = metal('#2a2e34', { roughness: 0.55, flatShading: true });
const DRONE_PLATE = metal('#8e97a2', { roughness: 0.3, flatShading: true });
const DRONE_COPPER = metal('#a4512f', { roughness: 0.4 });
const DRONE_LAMP = emis('#d9fbff', '#3fd4ff', 1.8);

/** The artificer's repair drone: an iron box with a lamp under it, two rotors, a welding arm; ticks spin the rotors. */
export const drone = (o?: BaseOpts): Model => {
  const c = begin(o, 0.35, 64);
  const { g } = c;
  g.position.y = 1.3;
  M(new THREE.BoxGeometry(0.34, 0.18, 0.26), DRONE_IRON, g);
  const lid = M(new THREE.BoxGeometry(0.36, 0.04, 0.28), DRONE_PLATE, g);
  lid.position.y = 0.11;
  M(new THREE.SphereGeometry(0.06, 8, 6), DRONE_LAMP, g).position.set(0, -0.1, 0);
  glowSprite(g, '#4fdcff', 0.4, V(0, -0.12, 0), 0.7);
  // The welding arm, down and forward.
  limb(g, V(0.1, -0.06, 0.1), V(0.16, -0.24, 0.22), 0.02, 0.014, DRONE_COPPER, 5);
  const rotors: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    limb(g, V(k * 0.12, 0.1, 0), V(k * 0.28, 0.18, 0), 0.018, 0.018, DRONE_IRON, 5);
    const r = G(g, k * 0.28, 0.21, 0);
    for (let i = 0; i < 2; i++) {
      const blade = M(new THREE.BoxGeometry(0.3, 0.01, 0.04), DRONE_PLATE, r);
      blade.rotation.y = (i * Math.PI) / 2;
    }
    rotors.push(r);
  }
  const t = (n: number): void => {
    rotors.forEach((r, i) => (r.rotation.y = n * (i ? 0.9 : -0.9)));
  };
  t(0);
  return done(c, 1.6, t);
};

const BANNER_CLOTH = cloth('#8e2420', { side: THREE.DoubleSide, flatShading: true });
const BANNER_TRIM = cloth('#d9dce2', { side: THREE.DoubleSide });
const BANNER_STEEL = metal('#a7aeb8', { roughness: 0.35 });

/** The warrior's guard banner: a tall pole driven into the ground, a long red banner with a pale stripe; ticks wave it. */
export const banner = (o?: BaseOpts): Model => {
  const c = begin(o, 0.4, 65);
  const { g } = c;
  limb(g, V(0, 0, 0), V(0, 3.0, 0), 0.05, 0.04, POST, 6);
  // The iron foot and the crossbar the banner hangs from, a spear point on top.
  limb(g, V(0, 0, 0), V(0, 0.35, 0), 0.075, 0.06, IRON, 6);
  limb(g, V(-0.05, 2.75, 0), V(0.85, 2.75, 0), 0.025, 0.025, POST, 5);
  const tip = M(new THREE.ConeGeometry(0.06, 0.28, 6), BANNER_STEEL, g);
  tip.position.y = 3.12;
  const flag = G(g, 0, 2.75, 0);
  const cl = M(new THREE.PlaneGeometry(0.8, 1.5, 4, 6), BANNER_CLOTH, flag);
  cl.position.set(0.42, -0.75, 0);
  const stripe = M(new THREE.PlaneGeometry(0.14, 1.5, 1, 6), BANNER_TRIM, flag);
  stripe.position.set(0.42, -0.75, 0.005);
  const boss = M(new THREE.CylinderGeometry(0.17, 0.17, 0.04, 10), BANNER_STEEL, flag);
  boss.position.set(0.42, -0.55, 0.03);
  boss.rotation.x = Math.PI / 2;
  const pos = cl.geometry.attributes.position as THREE.BufferAttribute;
  const base = Float32Array.from(pos.array as Float32Array);
  const t = (n: number): void => {
    for (let i = 0; i < pos.count; i++) {
      const y = (0.75 - (base[i * 3 + 1] as number)) / 1.5;
      const x = ((base[i * 3] as number) + 0.4) / 0.8;
      pos.setZ(i, Math.sin(n * 1.4 + y * 3 + x) * 0.08 * (0.3 + y));
    }
    pos.needsUpdate = true;
    stripe.position.z = 0.005 + Math.sin(n * 1.4 + 1.5) * 0.04;
  };
  t(0);
  return done(c, 3.2, t);
};

const HOUND_IRON = metal('#2a2e34', { roughness: 0.65, metalness: 0.5, flatShading: true });
const HOUND_PLATE = metal('#5a616b', { roughness: 0.55, metalness: 0.5, flatShading: true });
const HOUND_EYE = emis('#ffd0c4', '#ff4f3a', 2.2);

/** The artificer's clockwork hound: an iron dog of plates and pistons, a key turning in its back, red eyes; ticks trot its legs. */
export const hound = (o?: BaseOpts): Model => {
  const c = begin(o, 0.45, 66);
  const { g } = c;
  const body = G(g, 0, 0.55, 0);
  const torso = M(new THREE.BoxGeometry(0.34, 0.28, 0.72), HOUND_IRON, body);
  torso.position.z = -0.02;
  // Plates along its back and the winding key.
  for (let i = 0; i < 3; i++) M(new THREE.BoxGeometry(0.38, 0.05, 0.18), HOUND_PLATE, body).position.set(0, 0.16, -0.24 + i * 0.22);
  limb(body, V(0, 0.2, -0.1), V(0, 0.4, -0.1), 0.02, 0.02, DRONE_COPPER, 5);
  const key = G(body, 0, 0.42, -0.1);
  for (const k of [-1, 1]) M(new THREE.TorusGeometry(0.06, 0.015, 5, 10), DRONE_COPPER, key).position.x = k * 0.07;
  // The head: a long jaw, ears of plate, red eyes.
  const head = G(body, 0, 0.12, 0.42);
  M(new THREE.BoxGeometry(0.24, 0.2, 0.26), HOUND_IRON, head);
  const jaw = M(new THREE.BoxGeometry(0.18, 0.08, 0.22), HOUND_PLATE, head);
  jaw.position.set(0, -0.08, 0.16);
  for (const k of [-1, 1]) {
    const ear = M(new THREE.ConeGeometry(0.05, 0.14, 4), HOUND_PLATE, head);
    ear.position.set(k * 0.08, 0.15, -0.04);
    M(new THREE.SphereGeometry(0.03, 6, 4), HOUND_EYE, head).position.set(k * 0.07, 0.04, 0.13);
  }
  glowSprite(head, '#ff4f3a', 0.35, V(0, 0.04, 0.16), 0.5);
  const tail = limb(body, V(0, 0.08, -0.38), V(0, 0.3, -0.62), 0.025, 0.015, HOUND_IRON, 5);
  // Legs: thigh pistons, each pair swinging opposite.
  const legs: THREE.Group[] = [];
  for (const [x, z] of [[-0.14, 0.24], [0.14, 0.24], [-0.14, -0.26], [0.14, -0.26]] as [number, number][]) {
    const leg = G(body, x, -0.1, z);
    limb(leg, V(0, 0, 0), V(0, -0.45, 0.04), 0.045, 0.03, HOUND_IRON, 6);
    M(new THREE.SphereGeometry(0.05, 6, 4), DRONE_COPPER, leg);
    legs.push(leg);
  }
  const t = (n: number): void => {
    const a = [0.45, 0, -0.45, 0][n & 3] as number;
    legs.forEach((l, i) => (l.rotation.x = (i === 0 || i === 3 ? 1 : -1) * a));
    key.rotation.z = n * 0.8;
    tail.rotation.x = Math.sin(n * 1.5) * 0.2;
    body.position.y = 0.55 + [0.03, 0, 0.03, 0][n & 3]!;
  };
  t(0);
  return done(c, 1.1, t);
};

const BARK = std('#4a3424', { roughness: 0.95, flatShading: true });
const BARK_DK = std('#2f2117', { roughness: 0.95, flatShading: true });
const LEAF = std('#3f6a34', { roughness: 0.85, flatShading: true });
const LEAF_LT = std('#5f8a46', { roughness: 0.85, flatShading: true });
const SAP = emis('#c9f5a0', '#7fc06a', 1.6);

/** The druid's living tree: a trunk on root-feet, two branch arms, a crown of leaves and green eyes of sap; ticks sway it. */
export const treant = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 67);
  const { g } = c;
  const body = G(g, 0, 0, 0);
  limb(body, V(0, 0.2, 0), V(0, 1.6, 0), 0.28, 0.2, BARK, 7);
  // Root-feet spreading out.
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    limb(body, V(0, 0.35, 0), V(Math.cos(a) * 0.45, 0, Math.sin(a) * 0.45), 0.09, 0.04, BARK_DK, 5);
  }
  // A face: two sap eyes in the bark, a dark mouth.
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.05, 6, 4), SAP, body).position.set(k * 0.09, 1.25, 0.21);
  M(new THREE.BoxGeometry(0.14, 0.05, 0.05), BARK_DK, body).position.set(0, 1.08, 0.22);
  glowSprite(body, '#9df0c4', 0.5, V(0, 1.25, 0.25), 0.4);
  // Arms: crooked branches with a leafy clump at each end.
  const arms: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const arm = G(body, k * 0.2, 1.2, 0);
    limb(arm, V(0, 0, 0), V(k * 0.45, -0.15, 0.1), 0.08, 0.06, BARK, 5);
    limb(arm, V(k * 0.45, -0.15, 0.1), V(k * 0.6, -0.5, 0.18), 0.06, 0.04, BARK, 5);
    M(new THREE.IcosahedronGeometry(0.16, 0), LEAF, arm).position.set(k * 0.62, -0.55, 0.2);
    arms.push(arm);
  }
  // The crown.
  const crown = G(body, 0, 1.75, 0);
  for (const [x, y, z, r, m] of [[0, 0.1, 0, 0.42, LEAF], [-0.28, -0.05, 0.1, 0.28, LEAF_LT], [0.28, 0, -0.08, 0.3, LEAF_LT], [0.05, 0.32, -0.05, 0.26, LEAF]] as [number, number, number, number, THREE.Material][]) {
    M(new THREE.IcosahedronGeometry(r, 0), m, crown).position.set(x, y, z);
  }
  const t = (n: number): void => {
    body.rotation.z = Math.sin(n * 0.7) * 0.04;
    arms.forEach((a, i) => (a.rotation.x = Math.sin(n * 0.9 + i * 2) * 0.25));
    crown.rotation.y = Math.sin(n * 0.4) * 0.1;
  };
  t(0);
  return done(c, 2.3, t);
};

/*
 * The unique pieces' forms' own summons (docs/UNICAS.md §3): the bark bear the druid's old bark leaves, the ranger's
 * empty cloak and the spectre of the old garrison Ser Gaspar's hammer calls (Ser Gaspar himself, if the group heard
 * him). Opaque and flat-shaded like the rest; the spectre's pallor is its stone-pale colours and its cold glow, not a
 * translucent material. No gold.
 */

const MOSS = std('#4f6a34', { roughness: 0.95, flatShading: true });

/** El oso viejo: a bear of old bark on four stumps of root, moss on its back, sap for eyes; ticks walk it. */
export const barkBear = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 71);
  const { g } = c;
  const body = G(g, 0, 0.7, 0);
  const trunk = M(new THREE.IcosahedronGeometry(0.5, 1), BARK, body);
  trunk.scale.set(0.95, 0.85, 1.35);
  // Moss over the back, and a few twigs.
  const moss = M(new THREE.IcosahedronGeometry(0.42, 0), MOSS, body);
  moss.scale.set(0.9, 0.45, 1.2);
  moss.position.set(0, 0.32, -0.05);
  for (const [x, z] of [[-0.18, 0.25], [0.2, -0.1], [-0.05, -0.4]] as [number, number][]) limb(body, V(x, 0.4, z), V(x * 1.4, 0.75, z - 0.1), 0.03, 0.01, BARK_DK, 4);
  // The head: a snout of bark, ears like knots, eyes of sap.
  const head = G(body, 0, 0.18, 0.66);
  const skull = M(new THREE.IcosahedronGeometry(0.3, 0), BARK, head);
  skull.scale.set(1, 0.9, 1.1);
  const snout = M(new THREE.CylinderGeometry(0.12, 0.17, 0.3, 6), BARK_DK, head);
  snout.rotation.x = Math.PI / 2;
  snout.position.set(0, -0.06, 0.28);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.08, 6, 4), BARK_DK, head).position.set(k * 0.2, 0.24, -0.04);
    M(new THREE.SphereGeometry(0.045, 6, 4), SAP, head).position.set(k * 0.12, 0.08, 0.24);
  }
  glowSprite(head, '#9df0c4', 0.45, V(0, 0.08, 0.28), 0.45);
  // Legs: stumps of root, swinging in pairs.
  const legs: THREE.Group[] = [];
  for (const [x, z] of [[-0.3, 0.4], [0.3, 0.4], [-0.3, -0.42], [0.3, -0.42]] as [number, number][]) {
    const leg = G(body, x, -0.25, z);
    limb(leg, V(0, 0, 0), V(0, -0.45, 0.04), 0.13, 0.1, BARK, 6);
    for (let i = 0; i < 3; i++) limb(leg, V(0, -0.42, 0.04), V(Math.cos(i * 2.1) * 0.14, -0.47, 0.04 + Math.sin(i * 2.1) * 0.14), 0.04, 0.02, BARK_DK, 4);
    legs.push(leg);
  }
  const t = (n: number): void => {
    const a = [0.35, 0, -0.35, 0][n & 3] as number;
    legs.forEach((l, i) => (l.rotation.x = (i === 0 || i === 3 ? 1 : -1) * a));
    head.rotation.x = Math.sin(n * 1.3) * 0.08;
    body.position.y = 0.7 + [0.04, 0, 0.04, 0][n & 3]!;
  };
  t(0);
  return done(c, 1.45, t);
};

const SHADE = std('#23262e', { roughness: 0.9, flatShading: true });
const SHADE_LT = std('#3a3f4a', { roughness: 0.9, flatShading: true });
const SHADE_EYE = emis('#cfdcff', '#8fa8ff', 1.8);

/**
 * Lobo de sombra (the goatherd's pelt, docs/PANOPLIA.md §10): a lean wolf of dusk, dark slate with a paler ridge of fur,
 * cold pale eyes; ticks run it. Its glow and the wisps it sheds are drawn in the fight, not baked.
 */
export const shadeWolf = (o?: BaseOpts): Model => {
  const c = begin(o, 0.4, 73);
  const { g } = c;
  const body = G(g, 0, 0.5, 0);
  const torso = M(new THREE.IcosahedronGeometry(0.3, 1), SHADE, body);
  torso.scale.set(0.7, 0.7, 1.45);
  const ridge = M(new THREE.ConeGeometry(0.12, 0.7, 4), SHADE_LT, body);
  ridge.rotation.x = Math.PI / 2;
  ridge.position.set(0, 0.17, -0.02);
  const head = G(body, 0, 0.12, 0.45);
  M(new THREE.IcosahedronGeometry(0.17, 0), SHADE, head).scale.set(0.9, 0.85, 1.1);
  const snout = M(new THREE.ConeGeometry(0.08, 0.26, 5), SHADE, head);
  snout.rotation.x = Math.PI / 2;
  snout.position.set(0, -0.03, 0.2);
  for (const k of [-1, 1]) {
    const ear = M(new THREE.ConeGeometry(0.05, 0.14, 4), SHADE_LT, head);
    ear.position.set(k * 0.08, 0.15, -0.03);
    M(new THREE.SphereGeometry(0.025, 5, 4), SHADE_EYE, head).position.set(k * 0.07, 0.04, 0.12);
  }
  glowSprite(head, '#cfdcff', 0.3, V(0, 0.04, 0.14), 0.4);
  const tail = G(body, 0, 0.08, -0.42);
  M(new THREE.ConeGeometry(0.07, 0.4, 4), SHADE_LT, tail).rotation.x = -Math.PI / 2 - 0.5;
  const legs: THREE.Group[] = [];
  for (const [x, z] of [[-0.12, 0.25], [0.12, 0.25], [-0.12, -0.25], [0.12, -0.25]] as [number, number][]) {
    const leg = G(body, x, -0.12, z);
    limb(leg, V(0, 0, 0), V(0, -0.38, 0.03), 0.05, 0.03, SHADE, 4);
    legs.push(leg);
  }
  const t = (n: number): void => {
    const a = [0.5, 0, -0.5, 0][n & 3] as number;
    legs.forEach((l, i) => (l.rotation.x = (i === 0 || i === 3 ? 1 : -1) * a));
    tail.rotation.y = Math.sin(n * 1.5) * 0.3;
    body.position.y = 0.5 + [0.05, 0, 0.05, 0][n & 3]!;
  };
  t(0);
  return done(c, 0.95, t);
};

const CLOAK = cloth('#4a5242', { flatShading: true, side: THREE.DoubleSide });
const CLOAK_DK = cloth('#353b31', { flatShading: true, side: THREE.DoubleSide });
const CLOAK_PIN = metal('#9aa0a8', { roughness: 0.4 });

/** Capa vacía: the ranger's cloak left standing on its bow-stave, hood up and empty, stirring in the wind (ticks). */
export const cloak = (o?: BaseOpts): Model => {
  const c = begin(o, 0.45, 72);
  const { g } = c;
  limb(g, V(0, 0, 0), V(0, 1.55, 0), 0.03, 0.025, POST, 5);
  // The cloak: a cone of cloth hung from the shoulders, open at the front.
  const shape = new THREE.ConeGeometry(0.42, 1.25, 9, 3, true, 0.5, Math.PI * 2 - 1);
  const body = M(shape, CLOAK, g);
  body.position.y = 0.85;
  const pos = shape.attributes.position as THREE.BufferAttribute;
  const base = Float32Array.from(pos.array as Float32Array);
  // The hood, empty: a darker hollow where a face would be.
  const hood = M(new THREE.SphereGeometry(0.2, 9, 7, 0, Math.PI * 2, 0, Math.PI * 0.62), CLOAK_DK, g);
  hood.position.set(0, 1.5, -0.02);
  hood.rotation.x = -0.25;
  M(new THREE.SphereGeometry(0.035, 6, 4), CLOAK_PIN, g).position.set(0, 1.36, 0.17);
  const t = (n: number): void => {
    for (let i = 0; i < pos.count; i++) {
      const y = base[i * 3 + 1] as number;
      const w = (0.625 - y) / 1.25;
      pos.setX(i, (base[i * 3] as number) + Math.sin(n * 1.4 + y * 4) * 0.04 * w);
      pos.setZ(i, (base[i * 3 + 2] as number) + Math.cos(n * 1.1 + y * 3) * 0.05 * w);
    }
    pos.needsUpdate = true;
  };
  t(0);
  return done(c, 1.75, t);
};

// Smooth-shaded: flat shading on a self-lit material was a shader program of its own (one more in every fight with the
// guard, RENDIMIENTO.md «Trampas»); smooth, it shares the self-lit pieces' program.
const SPECTRE = emis('#c8d4e6', '#7f9cc8', 0.55);
const SPECTRE_DK = emis('#8a98b0', '#4f6488', 0.4);
const SPECTRE_RED = emis('#ff8a6e', '#ff4f3a', 1.2);

/**
 * La guardia de Gaspar: a spectre of the old garrison, pale and cold-lit: helm, mail, a tower shield and a spear;
 * `gaspar`: Ser Gaspar himself, taller, a plume of dragon red and his hammer. Ticks sway it (it hovers).
 */
export const gasparGuard = (o?: BaseOpts & { gaspar?: boolean }): Model => {
  const big = !!o?.gaspar;
  const c = begin(o, 0.5, big ? 74 : 73);
  const { g } = c;
  const body = G(g, 0, 0.2, 0);
  // No feet: the mail trails off into mist (a tapering skirt).
  const skirt = M(new THREE.ConeGeometry(0.34, 0.9, 8, 1, true), SPECTRE_DK, body);
  skirt.rotation.x = Math.PI;
  skirt.position.y = 0.5;
  const chest = M(new THREE.CylinderGeometry(0.27, 0.22, 0.6, 8), SPECTRE, body);
  chest.position.y = 1.15;
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.14, 7, 5), SPECTRE, body).position.set(k * 0.3, 1.4, 0);
  // The helm, a slit of red light for eyes.
  const helm = M(new THREE.CylinderGeometry(0.17, 0.19, 0.3, 8), SPECTRE, body);
  helm.position.y = 1.65;
  M(new THREE.ConeGeometry(0.18, 0.16, 8), SPECTRE_DK, body).position.y = 1.87;
  M(new THREE.BoxGeometry(0.2, 0.03, 0.04), SPECTRE_RED, body).position.set(0, 1.66, 0.17);
  glowSprite(body, '#dfe8ff', 1.1, V(0, 1.2, 0), 0.3);
  if (big) {
    // Ser Gaspar: the plume, and his hammer raised.
    limb(body, V(0, 1.9, 0), V(0, 2.25, -0.18), 0.05, 0.02, SPECTRE_RED, 5);
    const arm = G(body, 0.32, 1.38, 0.05);
    limb(arm, V(0, 0, 0), V(0.1, 0.55, 0.15), 0.025, 0.025, SPECTRE_DK, 5);
    M(new THREE.BoxGeometry(0.26, 0.16, 0.16), SPECTRE, arm).position.set(0.1, 0.6, 0.15);
  } else {
    // A guard's spear.
    limb(body, V(0.32, 0.5, 0.12), V(0.36, 2.1, 0.12), 0.02, 0.02, SPECTRE_DK, 5);
    M(new THREE.ConeGeometry(0.05, 0.2, 5), SPECTRE, body).position.set(0.36, 2.2, 0.12);
  }
  // The tower shield on its left arm, a red stripe down it.
  const shield = M(new THREE.BoxGeometry(0.06, 0.75, 0.42), SPECTRE_DK, body);
  shield.position.set(-0.36, 1.1, 0.12);
  M(new THREE.BoxGeometry(0.065, 0.7, 0.06), SPECTRE_RED, body).position.set(-0.36, 1.1, 0.12);
  const t = (n: number): void => {
    body.position.y = 0.2 + Math.sin(n * 0.9) * 0.06;
    body.rotation.z = Math.sin(n * 0.6) * 0.03;
  };
  t(0);
  return done(c, big ? 2.5 : 2.2, t);
};
